<template>
  <div>
    <wc-page-title title='Financial Reports'>
      <template v-slot:rightSide>
        <wc-burger-menu>
          <v-list-item @click='changeDate'>
            <v-list-item-icon>
              <v-icon color='info'>mdi-calendar</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Change Report Details</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click='handleExport' :disabled='transactions.length === 0'>
            <v-list-item-icon>
              <v-icon color='success'>mdi-file-excel</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Export to Excel</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </wc-burger-menu>
      </template>
    </wc-page-title>
    <v-container>
      <report-title
        title='Account Transactions (By Reconciliation)'
        :subtitle='subtitle'
      ></report-title>
      <br>
      <br>
      <div v-if='transactions.length > 0'>
        <div v-for='a in accountsInResult' :key='a.id' class='mb-6 pb-6'>
          <wc-data-box :title="a.name + ' - ' + a.account_type_display">
            <transactions-table
              :transactions='getTransactions(a)'
            ></transactions-table>
          </wc-data-box>
        </div>
      </div>
    </v-container>

    <wc-dialog
      title='Report Detail'
      :showDialog='showDialog'
      width='700'
      saveText='Generate Report'
      @save='setupReport'
      @cancel='showDialog = false'
      :loadingButtons='isLoading'
    >
      <template v-slot:dialog-form>
        <v-autocomplete
          :loading='isLoading'
          :items='accounts'
          item-text='name'
          item-value='id'
          v-model='new_account'
          label='Account (optional)'
          clearable
          dense
          outlined
        ></v-autocomplete>
      </template>
      <v-row dense>
        <v-col>
          <wc-datepicker
            :disabled='isLoading'
            label='From'
            v-model='new_from_date'
            removeClearOption
          ></wc-datepicker>
        </v-col>
        <v-col>
          <wc-datepicker
            :disabled='isLoading'
            label='To'
            v-model='new_to_date'
            removeClearOption
          ></wc-datepicker>
        </v-col>
      </v-row>
    </wc-dialog>
  </div>
</template>

<script>
import TransactionsTable from '@/modules/management/components/financial_reports/TransactionsTable';
import ReportTitle from '@/modules/management/components/financial_reports/ReportTitle';
import bookkeepingAPI from '@/api/accounting';
import moment from 'moment';
import _ from 'lodash';
import miscServices from '@/services/misc';

export default {
  name: 'account-transactions-by-reconciliation',
  data: () => ({
    isLoading: false,
    showDialog: false,
    new_account: null,
    account: null,
    from_date: null,
    to_date: null,
    new_from_date: null,
    new_to_date: null,
    transactions: [],
    accounts: []
  }),
  computed: {
    subtitle () {
      if (this.from_date && this.to_date) {
        return this.$options.filters.shortDate(this.from_date) + ' to ' + this.$options.filters.shortDate(this.to_date)
      }
      return 'For the period...'
    },
    accountsInResult () {
      const seen = new Set()
      return this.transactions
        .filter(t => {
          if (seen.has(t.account)) return false
          seen.add(t.account)
          return true
        })
        .map(t => t.account_data)
    }
  },
  mounted () {
    this.from_date = moment().format('YYYY-MM-DD').toString()
    this.to_date = moment().format('YYYY-MM-DD').toString()
    this.getData()
    this.resetCopy()
    this.showDialog = true
  },
  methods: {
    async getData () {
      this.isLoading = true
      this.accounts = await bookkeepingAPI.getActiveAccountsPicker()
      this.isLoading = false
    },
    resetCopy () {
      this.new_account = this.account
      this.new_to_date = this.to_date
      this.new_from_date = this.from_date
    },
    getTransactions (acct) {
      return this.transactions.filter(t => t.account === acct.id)
    },
    async setupReport () {
      this.isLoading = true
      this.account = this.new_account
      this.from_date = this.new_from_date
      this.to_date = this.new_to_date
      this.transactions = await bookkeepingAPI.getAccountTransactionsByReconciliation(
        this.from_date,
        this.to_date,
        this.account
      )
      this.isLoading = false
      this.showDialog = false
    },
    changeDate () {
      this.resetCopy()
      this.showDialog = true
    },
    handleExport () {
      const columnHeaders = ['Date', 'JE Num.', 'Description', 'Counted in P&L', 'Debits', 'Credits']
      const rows = []

      this.accountsInResult.forEach(acct => {
        const acctTransactions = this.getTransactions(acct)
        const isCredit = acct.account_type === 'L' || acct.account_type === 'R'
        const totalDebits = _.sumBy(acctTransactions, t => t.debit || 0)
        const totalCredits = _.sumBy(acctTransactions, t => t.credit || 0)
        const balance = isCredit
          ? totalCredits - totalDebits
          : totalDebits - totalCredits

        rows.push([`${acct.name} - ${acct.account_type_display}`, '', '', '', '', ''])
        rows.push(columnHeaders)
        acctTransactions.forEach(t => {
          rows.push([
            t.journal_entry_data.posting_date,
            `JE#: ${t.journal_entry_data.id}`,
            t.journal_entry_data.description,
            t.is_count_profit_loss ? 'Yes' : 'No',
            t.debit || '',
            t.credit || ''
          ])
        })
        rows.push(['', '', '', 'TOTALS', totalDebits || '', totalCredits || ''])
        rows.push(['', '', '', 'ACCOUNT BALANCE', '', balance])
        rows.push(['', '', '', '', '', ''])
      })

      const filename = `account-transactions-by-reconciliation_${this.from_date}_${this.to_date}`
      miscServices.downloadAsSpreadsheet([], rows, filename)
    }
  },
  components: {
    'report-title': ReportTitle,
    'transactions-table': TransactionsTable
  }
}
</script>
